// Dashboard Principal de Recebíveis
// FASE 4.1 - Interface principal com métricas e gráficos

'use client'

import React, { useState, useEffect } from 'react'
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Users, 
  Calendar,
  AlertCircle,
  BarChart3,
  PieChart,
  RefreshCw
} from 'lucide-react'
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart as RechartsPieChart,
  Cell
} from 'recharts'
import Layout from '../../../components/vision/Layout'
import ProtectedRoute from '../../../components/vision/ProtectedRoute'

export default function DashboardRecebiveis() {
  const [metricas, setMetricas] = useState(null)
  const [dadosGrafico, setDadosGrafico] = useState([])
  const [competenciasPorStatus, setCompetenciasPorStatus] = useState(null)
  const [loading, setLoading] = useState(true)
  const [erro, setErro] = useState('')

  useEffect(() => {
    carregarDadosDashboard()
  }, [])

  const carregarDadosDashboard = async () => {
    setLoading(true)
    setErro('')

    try {
      // Carregar métricas principais
      const responseMetricas = await fetch('/api/dashboard/metricas')
      const dadosMetricas = await responseMetricas.json()

      if (dadosMetricas.sucesso) {
        setMetricas(dadosMetricas.metricas)
      }

      // Carregar dados do gráfico
      const responseGrafico = await fetch('/api/dashboard/evolucao')
      const dadosEvolucao = await responseGrafico.json()

      if (dadosEvolucao.sucesso) {
        setDadosGrafico(dadosEvolucao.dados)
      }

      // Carregar competências por status
      const responseStatus = await fetch('/api/dashboard/competencias-status')
      const dadosStatus = await responseStatus.json()

      if (dadosStatus.sucesso) {
        setCompetenciasPorStatus(dadosStatus)
      }

    } catch (error) {
      console.error('Erro ao carregar dashboard:', error)
      setErro('Erro ao carregar dados do dashboard')
    } finally {
      setLoading(false)
    }
  }

  const formatarMoeda = (valor) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor || 0)
  }

  const formatarPorcentagem = (valor) => {
    return `${(valor || 0).toFixed(1)}%`
  }

  const coresStatus = {
    ativa: '#10B981',
    vencida: '#F59E0B', 
    paga: '#3B82F6',
    cancelada: '#EF4444',
    suspensa: '#6B7280'
  }

  if (loading) {
    return (
      <ProtectedRoute>
        <Layout>
          <div className="min-h-screen bg-gray-50 flex items-center justify-center">
            <div className="flex items-center space-x-3">
              <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
              <span className="text-gray-600">Carregando dashboard...</span>
            </div>
          </div>
        </Layout>
      </ProtectedRoute>
    )
  }

  return (
    <ProtectedRoute>
      <Layout>
        <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Dashboard de Recebíveis</h1>
              <p className="text-sm text-gray-600">Visão geral das métricas financeiras</p>
            </div>
            <button
              onClick={carregarDadosDashboard}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Atualizar</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {erro && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
              <span className="text-red-800">{erro}</span>
            </div>
          </div>
        )}

        {/* Cards de Métricas Principais */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* MRR */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">MRR</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatarMoeda(metricas?.mrr)}
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <div className="mt-4 flex items-center">
              {metricas?.mrr_crescimento >= 0 ? (
                <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
              ) : (
                <TrendingDown className="w-4 h-4 text-red-500 mr-1" />
              )}
              <span className={`text-sm font-medium ${
                metricas?.mrr_crescimento >= 0 ? 'text-green-600' : 'text-red-600'
              }`}>
                {formatarPorcentagem(metricas?.mrr_crescimento)} vs mês anterior
              </span>
            </div>
          </div>

          {/* ARR */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">ARR</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatarMoeda(metricas?.arr)}
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <BarChart3 className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <div className="mt-4">
              <span className="text-sm text-gray-600">
                Projeção: {formatarMoeda(metricas?.arr_projecao)}
              </span>
            </div>
          </div>

          {/* Taxa de Churn */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Taxa de Churn</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatarPorcentagem(metricas?.churn_rate)}
                </p>
              </div>
              <div className="p-3 bg-red-100 rounded-full">
                <TrendingDown className="w-6 h-6 text-red-600" />
              </div>
            </div>
            <div className="mt-4 flex items-center">
              {metricas?.churn_tendencia <= 0 ? (
                <TrendingDown className="w-4 h-4 text-green-500 mr-1" />
              ) : (
                <TrendingUp className="w-4 h-4 text-red-500 mr-1" />
              )}
              <span className={`text-sm font-medium ${
                metricas?.churn_tendencia <= 0 ? 'text-green-600' : 'text-red-600'
              }`}>
                {formatarPorcentagem(Math.abs(metricas?.churn_tendencia))} vs mês anterior
              </span>
            </div>
          </div>

          {/* Planos Ativos */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Planos Ativos</p>
                <p className="text-2xl font-bold text-gray-900">
                  {metricas?.planos_ativos || 0}
                </p>
              </div>
              <div className="p-3 bg-purple-100 rounded-full">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
            </div>
            <div className="mt-4">
              <span className="text-sm text-gray-600">
                +{metricas?.planos_novos_mes || 0} novos este mês
              </span>
            </div>
          </div>
        </div>

        {/* Gráficos */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Gráfico de Evolução Mensal */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Evolução Mensal</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={dadosGrafico}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="mes_nome" />
                <YAxis tickFormatter={(value) => `R$ ${(value / 1000).toFixed(0)}k`} />
                <Tooltip 
                  formatter={(value) => [formatarMoeda(value), '']}
                  labelFormatter={(label) => `Mês: ${label}`}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="receita_prevista" 
                  stroke="#3B82F6" 
                  strokeWidth={2}
                  name="Receita Prevista"
                />
                <Line 
                  type="monotone" 
                  dataKey="receita_realizada" 
                  stroke="#10B981" 
                  strokeWidth={2}
                  name="Receita Realizada"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Gráfico de Planos */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Novos Planos vs Cancelamentos</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={dadosGrafico}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="mes_nome" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="novos_planos" fill="#10B981" name="Novos Planos" />
                <Bar dataKey="cancelamentos" fill="#EF4444" name="Cancelamentos" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Seção de Receita e Vencimentos */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Receita do Mês */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Receita do Mês</h3>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-gray-600">Realizada</span>
                  <span className="font-semibold">{formatarMoeda(metricas?.receita_realizada)}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-green-600 h-2 rounded-full" 
                    style={{ 
                      width: `${Math.min(100, (metricas?.receita_realizada / metricas?.receita_meta) * 100)}%` 
                    }}
                  ></div>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Meta</span>
                <span className="font-semibold">{formatarMoeda(metricas?.receita_meta)}</span>
              </div>
              <div className="text-center">
                <span className="text-2xl font-bold text-gray-900">
                  {formatarPorcentagem((metricas?.receita_realizada / metricas?.receita_meta) * 100)}
                </span>
                <p className="text-sm text-gray-600">da meta atingida</p>
              </div>
            </div>
          </div>

          {/* Vencimentos Próximos */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Calendar className="w-5 h-5 mr-2" />
              Próximos Vencimentos
            </h3>
            <div className="space-y-3">
              <div className="text-center">
                <p className="text-2xl font-bold text-orange-600">
                  {metricas?.vencimentos_proximos || 0}
                </p>
                <p className="text-sm text-gray-600">competências</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-semibold text-gray-900">
                  {formatarMoeda(metricas?.vencimentos_valor)}
                </p>
                <p className="text-sm text-gray-600">próximos 7 dias</p>
              </div>
              <button className="w-full mt-4 px-4 py-2 bg-orange-100 text-orange-800 rounded-lg hover:bg-orange-200 transition-colors">
                Ver Detalhes
              </button>
            </div>
          </div>

          {/* Status das Competências */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <PieChart className="w-5 h-5 mr-2" />
              Status das Competências
            </h3>
            {competenciasPorStatus?.resumo && (
              <div className="space-y-3">
                {Object.entries(competenciasPorStatus.resumo).map(([status, dados]) => (
                  <div key={status} className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div 
                        className="w-3 h-3 rounded-full mr-2"
                        style={{ backgroundColor: coresStatus[status] }}
                      ></div>
                      <span className="text-sm capitalize">{status}</span>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold">{dados.quantidade}</p>
                      <p className="text-xs text-gray-600">
                        {formatarMoeda(dados.valor_total)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
        </div>
      </Layout>
    </ProtectedRoute>
  )
}
